export function createCookie(name, value, hours) {
  var date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  var expires = "; expires=" + date.toGMTString();
  document.cookie = escape(name) + "=" + escape(value) + expires + "; path=/";
}

/** Generate a UUID (version 4).
 * @param {boolean} dashed - If true, include dashes in the UUID.
 * @return {string} - The generated UUID.
 **/
export function generateUUID(dashed) {
  var d = new Date().getTime();
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    d += performance.now();
  }
  return (dashed
    ? "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
    : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx"
  ).replace(/[xy]/g, function(c) {
    var r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Get the combined height of the top navigation and any fixed items.
 * @return {number} - The total height in pixels.
 **/
export function getTopAreaHeight() {
  var mainNav = document.getElementsByClassName("megamenu");
  const navHeight = mainNav && mainNav.length ? mainNav[0].scrollHeight : 0;
  const fixedItems = Array.prototype.slice.call(
    document.querySelectorAll(
      ".metso-top-fix-on-scroll:not(.hide)"
    )
  );
  return (
    navHeight +
    fixedItems.reduce((acc, cur) => cur.scrollHeight + acc, 0)
  );
}

/** Get the outer width of an element, including margins.
 * @param {HTMLElement} el - The element to measure.
 * @param {boolean} withComputedStyle - If true, also return the computed style object.
 * @return {number|object} - The outer width in pixels, or an object with width and style.
 **/
export function outerWidth(el, withComputedStyle) {
  let width = el.offsetWidth;
  const style = getComputedStyle(el);
  width += parseInt(style.marginLeft) + parseInt(style.marginRight);
  return withComputedStyle ? { width: width, style: style } : width;
}

/**
 * Convert a relative URL to an absolute URL.
 * @param {string} href - The relative or absolute URL.
 * @return {string} - The absolute URL.
 **/
export function toAbsolute(href) {
  const link = document.createElement("a");
  link.href = href;
  return (
    link.protocol + "//" + link.host + link.pathname + link.search + link.hash
  );
}

/**
 * Truncate text to a maximum length without cutting words in half.
 * If truncation occurs, appends an ellipsis (…).
 * @param {string} fullText - The original text to truncate.
 * @param {number} maxLength - The maximum allowed length of the text.
 * @return {string} - The truncated text with ellipsis if applicable.
 **/
export function truncText(fullText, maxLength) {
  // https://www.npmjs.com/package/trunc-text
  var delimiter = "…";
  function _truncText(input, cap) {
    var limit = Number(cap);
    if (isNaN(limit)) {
      return delimiter;
    }
    if (input.length <= limit) {
      return input;
    }
    var result = input.substr(0, limit);
    var i = result.lastIndexOf(" ");
    if (i === -1) {
      // assume that we'd otherwise slash a word
      return delimiter;
    }
    // truncate up to the last space
    return result.substr(0, i) + delimiter;
  }
  if (!(typeof fullText === "string" && fullText.length && maxLength > 0)) {
    return fullText;
  }
  return _truncText(fullText, maxLength) || fullText;
}

/**
 * Unified Vue boot function.
 * @param {string} selector - CSS selector for root elements.
 * @param {object} component - Imported SFC (script only; template injected).
 * @param {object} options
 *   jsonProps: string[] - attribute (camelCase) names to force JSON parsing.
 *   ignore: string[] - attribute names to skip.
 *   beforeMount(el, props): void
 *   createAppWrapper(wrapped, props): App - custom app creation with plugins
 */
export function bootVue(selector, component, options = {}) {
  const {
    jsonProps = [],
    ignore = ["id", "class"],
    beforeMount,
    createAppWrapper
  } = options;

  const toCamel = str => str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

  const shouldAttemptJson = (propName, raw) => {
    if (jsonProps.includes(propName)) return true;
    if (typeof raw !== "string") return false;
    const t = raw.trim();
    return t.startsWith("{") || t.startsWith("[");
  };

  const parseJsonSafe = raw => {
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const castValue = (propName, raw) => {
    if (raw === "true") return true;
    if (raw === "false") return false;
    if (/^-?\d+(\.\d+)?$/.test(raw) && !jsonProps.includes(propName)) {
      const num = Number(raw);
      if (!isNaN(num)) return num;
    }
    if (shouldAttemptJson(propName, raw)) return parseJsonSafe(raw);
    return raw;
  };

  document.querySelectorAll(selector).forEach(el => {

    const template = el.innerHTML;
    const extractedProps = {};

    [...el.attributes].forEach(attr => {
      const name = attr.name;
      if (
        ignore.includes(name) ||
        name.startsWith("v-") ||
        name.startsWith("@") ||
        name.startsWith(":")
      ) return;
      const propName = toCamel(name);
      extractedProps[propName] = castValue(propName, attr.value);
    });

    jsonProps.forEach(p => {
      if (Array.isArray(extractedProps[p])) return;
      if (typeof extractedProps[p] === "string") {
        const parsed = parseJsonSafe(extractedProps[p]);
        extractedProps[p] = Array.isArray(parsed) ? parsed : [];
      }
    });

    beforeMount?.(el, extractedProps);

    el.innerHTML = "";

    const wrapped = { ...component, template };
    const app = createAppWrapper 
      ? createAppWrapper(wrapped, extractedProps)
      : createApp(wrapped, extractedProps);
    app.mount(el);
  });
}

export default {
  createCookie,
  generateUUID,
  getTopAreaHeight,
  outerWidth,
  toAbsolute,
  truncText,
  bootVue
};
